// Core API Request Function with Token Refresh
import { API_BASE_URL, createHeaders, apiUtils, getAuthToken } from './config.js';

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise = null;

// Forward declaration - will be set by auth module
let authAPIRefreshToken = null;

// Track active requests for potential cancellation
const activeRequests = new Map();

export const setAuthRefreshToken = (fn) => {
    authAPIRefreshToken = fn;
};

/**
 * Cancel a pending request by key
 * @param {string} key - Request identifier
 */
export const cancelRequest = (key) => {
    const controller = activeRequests.get(key);
    if (controller) {
        controller.abort();
        activeRequests.delete(key);
    }
};

/**
 * Cancel all pending requests matching a pattern
 * @param {string} pattern - Pattern to match (e.g., 'search:')
 */
export const cancelRequestsByPattern = (pattern) => {
    for (const [key, controller] of activeRequests.entries()) {
        if (key.includes(pattern)) {
            controller.abort();
            activeRequests.delete(key);
        }
    }
};

// Generic API request function with automatic token refresh
export const apiRequest = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;

    // Create AbortController for this request if a requestKey is provided
    let abortController = null;
    if (options.requestKey) {
        // Cancel any existing request with the same key
        cancelRequest(options.requestKey);
        abortController = new AbortController();
        activeRequests.set(options.requestKey, abortController);
    }

    // Headers finales: 3 fuentes mergeadas en orden de precedencia
    // creciente. La forma vieja era spread `...options` sobre `headers:`,
    // que pisaba todo. El bugfix posterior `config.headers = headers`
    // pisaba options.headers (que callsites como businessAPI.getById usan
    // para mandar Bearer token cuando requireAuth=false). Esta forma
    // explicita preserva ambos comportamientos.
    const baseHeaders = createHeaders(options.requireAuth !== false);
    if (options.body instanceof FormData) {
        delete baseHeaders['Content-Type'];
    }
    const finalHeaders = {
        ...baseHeaders,
        ...(options.headers || {}),      // caller-supplied (ej. Bearer manual)
        ...(options.extraHeaders || {}), // F3 Idempotency-Key etc.
    };

    const config = {
        ...options,
        headers: finalHeaders,
        // Force no-cache by default to avoid 304 responses for API endpoints
        cache: options.cache || 'no-store',
        ...(abortController && { signal: abortController.signal }),
    };

    // Remove custom options that shouldn't be passed to fetch
    delete config.requestKey;
    delete config.requireAuth;
    delete config.skipRefreshOn403;
    delete config.extraHeaders;

    try {
        const response = await fetch(url, config);

        // Cleanup the request from active map
        if (options.requestKey) {
            activeRequests.delete(options.requestKey);
        }

        // Sesión huérfana: 401 en endpoint autenticado + currentUser presente + authToken vacío.
        // Pasaba en el caso Grupo Paint (Metal Paint SPA #325) — el token se perdió pero
        // currentUser quedó en localStorage, el frontend seguía haciendo polls que retornaban
        // 401 indefinidamente sin refresh ni redirect. La única salida real es forzar re-login.
        const isOrphanSession = response.status === 401
            && options.requireAuth !== false
            && !endpoint.includes('/auth/')
            && !getAuthToken()
            && !!localStorage.getItem('currentUser');
        if (isOrphanSession) {
            apiUtils.clearAuth();
            window.location.href = '/';
            throw new Error('Session expired. Please log in again.');
        }

        // Check if token is expired (401 only)
        // 403 is a permission denial, NOT a token issue - don't try to refresh
        // Only try refresh if we actually have a token — visitors without session should never be redirected
        const shouldTryRefresh = response.status === 401
            && options.requireAuth !== false
            && !endpoint.includes('/auth/')
            && !!getAuthToken();

        if (shouldTryRefresh) {
            // Try to refresh token if not already refreshing
            if (!isRefreshing && authAPIRefreshToken) {
                isRefreshing = true;
                try {
                    refreshPromise = authAPIRefreshToken();
                    await refreshPromise;

                    // Retry the original request with new token
                    const retryHeaders = createHeaders(true);
                    // Preserve FormData Content-Type handling
                    if (options.body instanceof FormData) {
                        delete retryHeaders['Content-Type'];
                    }
                    const newConfig = {
                        ...config,
                        headers: retryHeaders
                    };

                    const retryResponse = await fetch(url, newConfig);

                    // Handle no-content responses
                    if (retryResponse.status === 204 || retryResponse.status === 304) {
                        return {};
                    }

                    // Safely parse response
                    const retryContentType = retryResponse.headers.get('content-type') || '';
                    let retryData = {};
                    if (retryContentType.includes('application/json')) {
                        try {
                            retryData = await retryResponse.json();
                        } catch (e) {
                            retryData = {};
                        }
                    }

                    if (!retryResponse.ok) {
                        // If retry also fails with 401, token is truly invalid
                        if (retryResponse.status === 401) {
                            apiUtils.clearAuth();
                            window.location.href = '/';
                            throw new Error('Session expired. Please log in again.');
                        }
                        throw new Error(retryData.error || `HTTP error! status: ${retryResponse.status}`);
                    }

                    return retryData;
                } catch (refreshError) {
                    // If refresh fails, clear auth and redirect
                    if (refreshError.message?.includes('Session expired')) {
                        throw refreshError;
                    }
                    apiUtils.clearAuth();
                    window.location.href = '/';
                    throw new Error('Session expired. Please log in again.');
                } finally {
                    isRefreshing = false;
                    refreshPromise = null;
                }
            } else if (refreshPromise) {
                // Wait for ongoing refresh to complete
                try {
                    await refreshPromise;
                    // Retry the original request with new token
                    const retryHeaders = createHeaders(true);
                    if (options.body instanceof FormData) {
                        delete retryHeaders['Content-Type'];
                    }
                    const newConfig = {
                        ...config,
                        headers: retryHeaders
                    };
                    const retryResponse = await fetch(url, newConfig);

                    if (retryResponse.status === 204 || retryResponse.status === 304) {
                        return {};
                    }

                    const retryContentType = retryResponse.headers.get('content-type') || '';
                    let retryData = {};
                    if (retryContentType.includes('application/json')) {
                        try {
                            retryData = await retryResponse.json();
                        } catch (e) {
                            retryData = {};
                        }
                    }

                    if (!retryResponse.ok) {
                        throw new Error(retryData.error || `HTTP error! status: ${retryResponse.status}`);
                    }

                    return retryData;
                } catch (error) {
                    throw new Error('Session expired. Please log in again.');
                }
            }
        }

        // Handle responses with no content (204) or Not Modified (304)
        if (response.status === 204 || response.status === 304) {
            return {};
        }

        // Only attempt to parse JSON when content-type indicates JSON
        const contentType = response.headers.get('content-type') || '';
        let data = {};
        if (contentType.includes('application/json')) {
            try {
                data = await response.json();
            } catch (parseErr) {
                console.error('Failed to parse JSON response for', endpoint, parseErr);
                data = {};
            }
        }

        if (!response.ok) {
            let errorMsg = (data && data.error);
            // fieldErrors — mapa {campo: 'msg'} para que la UI pueda pintar
            // el input específico. Sin esto la UI muestra todos los errores
            // como un banner rojo genérico ("email: X | tax_id: Y | ...")
            // sin poder asociarlos al input. Se llena desde data.details
            // (nuestro formato) O data como field errors de DRF.
            const fieldErrors = {};
            const collect = (obj) => {
                if (!obj || typeof obj !== 'object') return;
                for (const [key, val] of Object.entries(obj)) {
                    if (key === 'error' || key === 'details' || key === 'code' ||
                        key === 'request_id' || key === 'non_field_errors') continue;
                    if (Array.isArray(val)) {
                        const msg = val.filter(v => typeof v === 'string').join(', ');
                        if (msg) fieldErrors[key] = msg;
                    } else if (typeof val === 'string') {
                        fieldErrors[key] = val;
                    }
                }
            };
            if (data && data.details && typeof data.details === 'object') collect(data.details);
            if (data && typeof data === 'object') collect(data);

            if (!errorMsg) {
                const parts = Object.entries(fieldErrors).map(([k, v]) => `${k}: ${v}`);
                // non_field_errors de DRF: mensaje "sin campo" — mostrar suelto.
                if (data && Array.isArray(data.non_field_errors)) {
                    parts.push(...data.non_field_errors.filter(v => typeof v === 'string'));
                }
                if (parts.length > 0) errorMsg = parts.join(' | ');
            }

            const err = new Error(errorMsg || `HTTP error! status: ${response.status}`);
            err.status = response.status;
            err.data = data;
            err.fieldErrors = fieldErrors;
            throw err;
        }

        return data;
    } catch (error) {
        // Don't log aborted requests as errors
        if (error.name === 'AbortError') {
            if (import.meta.env.DEV) {
                console.log(`Request cancelled: ${endpoint}`);
            }
            throw error;
        }
        console.error(`API request failed: ${endpoint}`, error);
        throw error;
    }
};
